import { useState, useEffect } from 'react';
import { Button } from '@geberit/gdds';

// styles
import styles from './image-gallery.module.scss';

// types
import type { ItemDefaultProps, OverlayGroup } from '../types';

// components
import { Overlay } from './overlay';
import { Image } from 'components/image/image';

// utils
import { useIsDesktop, useIsMobile } from 'components/App/SizeProvider';
import { decodingContent } from 'utils/decodingContent';

export function GalleryItem({ item, setCurrentItem }: Readonly<ItemDefaultProps>) {
  const isMobile = useIsMobile();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const { pictureObject, pictureAlt, picture, pictureCaption } = item;
  const overlay = item.overlay as OverlayGroup;
  const overlayHasData = Boolean(overlay?.title || overlay?.subtitle || overlay?.text);

  // close overlay when switching between mobile and desktop/tablet
  useEffect(() => {
    setOverlayOpen(false);
    setCurrentItem(undefined);
  }, [isMobile, setCurrentItem]);

  const handleOpenOverlay = () => {
    if (isMobile) {
      setCurrentItem(overlay);
    } else {
      setOverlayOpen(true);
    }
  };

  const onKeyUp = (e) => {
    if (e.key === 'Enter') {
      handleOpenOverlay();
    }
  };

  return (
    <div className={styles.galleryItem}>
      <div className={styles.imageWrapper}>
        <Image
          data={pictureObject}
          alt={pictureAlt}
          title={pictureAlt}
          src={picture}
          format={pictureObject.format}
          aspectRatio={pictureObject.aspectRatio}
        />

        {/* show info icon when overlay information are available */}
        {overlayHasData && (
          <Button
            className={styles.infoIcon}
            isIcon
            rounded
            size="tiny"
            stylingType="iconHighlight"
            symbol="warning_info"
            onClick={handleOpenOverlay}
            onKeyUp={onKeyUp}
          />
        )}

        {overlayOpen && (
          <Overlay
            title={overlay.title}
            subtitle={overlay.subtitle}
            emphasize={overlay.emphasize}
            text={overlay.text}
            onClose={() => setOverlayOpen(false)}
          />
        )}
      </div>

      {pictureCaption && <span className={styles.caption}>{decodingContent(pictureCaption)}</span>}
    </div>
  );
}
